// Fiche de match repensée (D-058, maquettes validées) : un en-tête selon le moment (à venir,
// en direct, terminé), des onglets Avant / Direct / Après ouverts d'office au bon moment, la
// liste « À faire » du coach avant le match, et la carte du résultat à partager après.
import { matchPhotos, matchRemind, type ClubBrand, type Convoked, type MatchPhoto } from '../lib/api';
import type { Attendance, AttendanceResponse } from '../domain/attendance';
import { agree, type TeamGender } from '../domain/labels';
import type { Phase } from '../domain/match-phase';
import { $, $$, html, mount, toast, type Html } from './dom';
import { convocationReach } from './reach';
import { oppCrest } from './crest';

const QLABEL = (q: number) => (q <= 4 ? `${q}e quart-temps` : `prolongation ${q - 4}`);
const upper = (s: string) => s.toLocaleUpperCase('fr-FR');

export interface HeroInput {
  phase: Phase; staff: boolean; club: ClubBrand | null;
  team: string; opponent: string; isHome: boolean | null; competition: string;
  start: Date; dayLabel: string; time: string; location: string | null;
  live: { us: number; them: number; quarter: number; quarters: { q: number; us: number; them: number }[] } | null;
  result: { kind: 'win' | 'loss' | 'draw'; text: string } | null;
  countdown: string | null;
  /** Joueuse ou joueur : retenu (true) ou non (false) dans la composition annoncée ; null : pas annoncée. */
  retained: boolean | null;
  /** Genre de l'équipe, pour accorder « retenu » / « retenue » (D-008, D-101). */
  gender?: TeamGender;
  /** Logo du club adverse (annuaire des clubs, D-094) ; null : son monogramme. */
  opponentLogo?: string | null;
}

const crest = (club: ClubBrand | null, cls = ''): Html => club?.logo_url
  ? html`<img class="mh-crest ${cls}" src="${club.logo_url}" alt="">`
  : html`<span class="mh-crest mh-mono ${cls}">${(club?.short_name ?? club?.name ?? '?').slice(0, 3)}</span>`;

/** La petite pastille de l'en-tête de page : « J-3 », « EN DIRECT », « TERMINÉ ». */
export function heroChip(h: HeroInput): Html {
  if (h.live) return html`<span class="chip chip-ko">● En direct</span>`;
  if (h.result) return html`<span class="chip chip-ok">Terminé</span>`;
  return h.countdown ? html`<span class="chip chip-mute">${h.countdown}</span>` : html``;
}

export function matchHero(h: HeroInput): Html {
  const side = h.isHome === false ? 'Extérieur' : h.isHome ? 'Domicile' : '';
  const versus = `${h.isHome === false ? 'à' : 'contre'} ${h.opponent}`;
  const opp = (cls: string) => oppCrest({ opponent: h.opponent }, cls, h.opponentLogo);
  if (h.live) return html`
    <section class="mh mh-dark" role="status" aria-live="polite">
      <div class="mh-top"><span>${upper(`${h.team} · ${h.competition}${h.isHome === false ? ` · à ${h.opponent}` : ''}`)}</span><span class="mh-hot">${upper(QLABEL(h.live.quarter))}</span></div>
      <div class="mh-board">
        <div>${crest(h.club, 'mh-crest-sm')}<span class="mh-team">${upper(h.team)}</span><b>${h.live.us}</b></div>
        <span class="mh-sep">–</span>
        <div>${opp('mh-crest-sm mh-on-dark')}<span class="mh-team">${upper(h.opponent)}</span><b class="mh-them">${h.live.them}</b></div>
      </div>
      ${h.live.quarters.length ? html`<div class="mh-qs">${h.live.quarters.map(q => html`<span class="${q.q === h.live!.quarter ? 'on' : ''}">Q${q.q} ${q.us}-${q.them}</span>`)}</div>` : ''}
    </section>`;
  if (h.result) return html`
    <section class="mh mh-club">
      <div class="mh-top"><span>${upper(`${h.team} · ${h.competition} · ${h.dayLabel}`)}</span></div>
      <div class="mh-result"><span>${h.result.kind === 'win' ? 'VICTOIRE' : h.result.kind === 'loss' ? 'DÉFAITE' : 'MATCH NUL'}</span><b>${h.result.text}</b></div>
      <p class="mh-sub mh-opp-line">${opp('mh-crest-xs')}<span>${versus}</span></p>
    </section>`;
  if (h.staff) return html`
    <section class="mh mh-dark">
      <div class="mh-top"><span>${upper(`${h.team} · ${h.competition}`)}</span><span>${upper(side)}</span></div>
      <div class="mh-vs">
        <div>${crest(h.club)}<span class="mh-team">${upper(h.team)}</span></div>
        <div class="mh-when"><b>${upper(h.time)}</b><span>${upper(h.dayLabel)}</span></div>
        <div>${opp('mh-crest mh-on-dark')}<span class="mh-team">${upper(h.opponent)}</span></div>
      </div>
      ${h.location ? html`<p class="mh-sub">${h.location}</p>` : ''}
    </section>`;
  return html`
    <section class="mh mh-club">
      <div class="mh-top"><span>${upper(`${h.team} · ${h.countdown ? `match ${h.countdown}` : 'match'}${side ? ` · ${side}` : ''}`)}</span></div>
      <div class="mh-title-row">${opp('mh-crest')}<h2 class="mh-title">${upper(versus)}</h2></div>
      <p class="mh-meta">${upper(`${h.dayLabel} · ${h.time}${h.location ? ` · ${h.location}` : ''}`)}</p>
      ${h.retained !== null ? html`<div class="mh-chips"><span class="mh-pill ${h.retained ? 'ok' : ''}">${h.retained ? `✓ ${agree(h.gender ?? 'X', 'Retenu', 'Retenue')} pour ce match` : `Pas ${agree(h.gender ?? 'X', 'retenu', 'retenue')} cette fois`}</span></div>` : ''}
    </section>`;
}

/** Les onglets du moment ; `on` : l'onglet ouvert. */
export const phaseTabs = (on: Phase): Html => html`
  <div class="seg mp-tabs" role="tablist" aria-label="Moment du match">${(['avant', 'direct', 'apres'] as Phase[]).map(p => html`
    <button type="button" role="tab" data-phase="${p}" aria-selected="${String(p === on)}">${p === 'avant' ? 'Avant' : p === 'direct' ? 'Direct' : 'Après'}</button>`)}</div>`;

export interface TodoInput {
  eventId: string; date: string; dayName: string; away: boolean; gender: TeamGender;
  roster: Convoked[]; responses: AttendanceResponse[]; att: Attendance;
  nameOf: (pid: string) => string; statman: string | null;
  /** Emmène vers un bloc de la fiche (prépa, composition, covoiturage, présences). */
  go: (target: 'prep' | 'compo' | 'car' | 'sheet') => void;
}

/** Coach, avant le match : ce qui reste à faire, chacun avec son bouton. D-121 : réduit à la convocation et aux réponses ;
 *  la préparation, la composition, les stats et le covoiturage restent dans la page, sans compter comme du retard. */
export async function renderMatchTodo(el: HTMLElement, t: TodoInput): Promise<void> {
  const reach = await convocationReach(t.eventId, t.date).catch(() => null);   // « N sans l'app · partager sur WhatsApp » (D-103)
  const answered = new Set(t.responses.map(r => r.personId));
  const absent = new Set(t.att.absent.map(a => a.personId));
  const silent = t.roster.map(r => r.person_id).filter(pid => !answered.has(pid) && !absent.has(pid));
  // Les incertaines (D-116) : à confirmer, relancées toutes seules la veille du match.
  const unsure = t.att.uncertain;
  const renforts = t.roster.filter(r => r.invited).length;
  type Item = { done: boolean; title: string; sub: string; cta?: string; act?: string; primary?: boolean };
  const items: Item[] = [
    { done: true, title: 'Convocation envoyée', sub: `${t.roster.length} ${agree(t.gender, 'convoqué', 'convoquée', t.roster.length)}${renforts ? `, dont ${renforts} en renfort` : ''}${reach ? ` · ${reach.label} · partager sur WhatsApp` : ''}`,
      ...(reach ? { cta: 'WhatsApp', act: 'reach' } : {}) },
    silent.length
      ? { done: false, title: `${silent.length} ${silent.length > 1 ? 'réponses manquantes' : 'réponse manquante'}`, sub: `${silent.slice(0, 3).map(t.nameOf).join(', ')}${silent.length > 3 ? '…' : ''} · présentes par défaut`, cta: 'Relancer', act: 'remind' }
      : { done: true, title: agree(t.gender, 'Tous ont répondu', 'Toutes ont répondu'), sub: `${t.att.present.length} ${agree(t.gender, 'présent', 'présente', t.att.present.length)}` },
    ...(unsure.length ? [{ done: false, title: `${unsure.length} ${agree(t.gender, 'incertain', 'incertaine', unsure.length)} à confirmer`,
      sub: `${unsure.slice(0, 3).map(u => `${t.nameOf(u.personId)}${u.reason ? ` (« ${u.reason.charAt(0).toLowerCase()}${u.reason.slice(1)} »)` : ''}`).join(', ')}${unsure.length > 3 ? '…' : ''} · relancée${unsure.length > 1 ? 's' : ''} la veille`,
      cta: 'Voir', act: 'sheet' }] : []),
  ];
  const done = items.filter(i => i.done).length;
  mount(el, html`
    <section class="block mt-todo">
      <div class="block-head"><h3>À faire avant ${t.dayName}</h3><span class="mt-count">${done} / ${items.length}</span></div>
      <div class="mt-bar" aria-hidden="true"><span style="width:${Math.round(100 * done / items.length)}%"></span></div>
      <ul class="mt-list">${items.map(i => html`<li class="${i.done ? 'done' : ''}">
        <span class="mt-dot" aria-hidden="true">${i.done ? '✓' : ''}</span>
        <span class="mt-txt"><strong>${i.title}</strong><span>${i.sub}</span></span>
        ${i.cta ? html`<button type="button" class="btn btn-sm ${i.primary || !i.done ? 'btn-primary' : 'btn-soft'}" data-guide="mp-${i.act}" data-todo="${i.act}">${i.cta}</button>` : ''}
      </li>`)}</ul>
    </section>`);
  for (const b of $$<HTMLButtonElement>(el, '[data-todo]')) b.addEventListener('click', async () => {
    const a = b.dataset.todo;
    if (a === 'remind') {
      b.disabled = true;
      try { const n = await matchRemind(t.eventId, t.date); toast(n ? `Relance envoyée (${n} ${n > 1 ? 'comptes' : 'compte'})` : 'Déjà relancées aujourd’hui'); }
      catch { toast('Relance impossible pour le moment.', 'ko'); b.disabled = false; }
    } else if (a === 'reach') void reach?.open();
    else if (a === 'sheet') t.go(a);
  });
}

export interface ShareInput {
  eventId: string; club: ClubBrand | null; team: string; opponent: string; isHome: boolean | null;
  result: 'win' | 'loss' | 'draw'; us: number | null; them: number | null; scoreText: string; dayLabel: string;
  top: { name: string; pts: number } | null;
  /** Logo du club adverse (annuaire des clubs, D-094). */
  opponentLogo?: string | null;
}

/** Après le match : la carte du résultat, aux couleurs du club, à partager (Instagram, WhatsApp). */
export async function renderResultShare(el: HTMLElement, s: ShareInput): Promise<void> {
  const photos = await matchPhotos(s.eventId).catch(() => [] as MatchPhoto[]);
  const photo = photos.find(p => p.public) ?? photos[0] ?? null;
  const word = s.result === 'win' ? 'VICTOIRE' : s.result === 'loss' ? 'DÉFAITE' : 'MATCH NUL';
  mount(el, html`
    <section class="block mt-share">
      <div class="mt-card" aria-label="Aperçu de la carte du résultat">
        ${s.club?.logo_url ? html`<img src="${s.club.logo_url}" alt="">` : html`<span></span>`}
        <div><b>${word}</b><strong>${s.scoreText}</strong><small>${s.opponentLogo ? html`<img class="mt-opp" src="${s.opponentLogo}" alt="">` : ''}${s.team} · ${s.isHome === false ? 'à' : 'c.'} ${s.opponent}</small></div>
        ${s.top ? html`<em>${upper(`${s.top.name} · ${s.top.pts} pts`)}</em>` : html`<em></em>`}
      </div>
      <div class="mt-share-txt">
        <strong>La carte du résultat</strong>
        <p class="muted small">Aux couleurs du club, prête pour Instagram ou WhatsApp${photo ? ', sur le selfie de victoire' : ''}.</p>
        <button type="button" class="btn btn-primary btn-sm" id="mt-share">Partager</button>
      </div>
    </section>`);
  $(el, '#mt-share')!.addEventListener('click', async () => {
    try {
      const blob = await drawCard(s, word, photo?.data ?? null);
      const file = new File([blob], `resultat-${s.team.replace(/\W+/g, '-').toLowerCase()}.png`, { type: 'image/png' });
      if (navigator.canShare?.({ files: [file] })) { await navigator.share({ files: [file], title: `${word} ${s.scoreText}` }); return; }
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = file.name;
      document.body.append(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 30000);
      toast('Image enregistrée');
    } catch (e) { if ((e as Error).name !== 'AbortError') toast('Carte impossible pour le moment.', 'ko'); }
  });
}

const loadImg = (src: string) => new Promise<HTMLImageElement>((ok, ko) => { const i = new Image(); i.onload = () => ok(i); i.onerror = ko; i.src = src; });

/** La carte, en 1080 × 1350 (format d'un post) : couleur du club, écusson, résultat, score. */
async function drawCard(s: ShareInput, word: string, photo: string | null): Promise<Blob> {
  const W = 1080, H = 1350;
  const c = document.createElement('canvas');
  c.width = W; c.height = H;
  const g = c.getContext('2d')!;
  // La couleur du club, sinon celle que l'app affiche (thème appliqué), sinon un rouge basket.
  const shown = getComputedStyle(document.documentElement).getPropertyValue('--club-l-fill').trim();
  const color = [s.club?.primary_color ?? '', shown].find(c => /^#[0-9a-f]{6}$/i.test(c)) ?? '#C41E3A';
  await Promise.all([document.fonts.load('400 200px Anton'), document.fonts.load('700 40px "JetBrains Mono"')]).catch(() => undefined);
  g.fillStyle = color; g.fillRect(0, 0, W, H);
  if (photo) {
    const img = await loadImg(`data:image/jpeg;base64,${photo}`).catch(() => null);
    if (img) {
      const k = Math.max(W / img.width, H / img.height);
      g.drawImage(img, (W - img.width * k) / 2, (H - img.height * k) / 2, img.width * k, img.height * k);
      const grad = g.createLinearGradient(0, H * 0.35, 0, H);
      grad.addColorStop(0, 'rgba(0,0,0,0.05)'); grad.addColorStop(1, `${color}F2`);
      g.fillStyle = grad; g.fillRect(0, 0, W, H);
    }
  } else {
    // Le ballon en filigrane, comme sur l'app.
    g.strokeStyle = 'rgba(255,255,255,0.16)'; g.lineWidth = 10;
    g.beginPath(); g.arc(W - 120, 260, 420, 0, Math.PI * 2); g.stroke();
    g.beginPath(); g.moveTo(W - 540, 260); g.lineTo(W + 300, 260); g.stroke();
  }
  if (s.club?.logo_url) {
    const logo = await loadImg(s.club.logo_url).catch(() => null);
    if (logo) {
      g.save(); g.beginPath(); g.arc(150, 150, 90, 0, Math.PI * 2); g.closePath(); g.fillStyle = '#FFFFFF'; g.fill(); g.clip();
      g.drawImage(logo, 60, 60, 180, 180); g.restore();
    }
  }
  // Le logo du club adverse, à côté du nôtre.
  if (s.opponentLogo) {
    const opp = await loadImg(s.opponentLogo).catch(() => null);
    if (opp) {
      const x = s.club?.logo_url ? 300 : 150, k = Math.min(110 / opp.width, 110 / opp.height);
      g.save(); g.beginPath(); g.arc(x, 165, 66, 0, Math.PI * 2); g.closePath(); g.fillStyle = '#FFFFFF'; g.fill(); g.clip();
      g.drawImage(opp, x - (opp.width * k) / 2, 165 - (opp.height * k) / 2, opp.width * k, opp.height * k); g.restore();
    }
  }
  g.fillStyle = '#FFFFFF'; g.textBaseline = 'alphabetic';
  g.font = '700 38px "JetBrains Mono", monospace';
  g.fillText(upper(`${s.team} · ${s.dayLabel}`), 72, H - 560);
  g.font = '400 190px Anton, Impact, sans-serif';
  g.fillText(word, 64, H - 360);
  g.font = '400 230px Anton, Impact, sans-serif';
  g.fillText(s.scoreText.replace(/\s*–\s*/, ' – '), 64, H - 150);
  g.font = '600 46px Inter, system-ui, sans-serif';
  g.fillText(`${s.isHome === false ? 'à' : 'contre'} ${s.opponent}`, 72, H - 80);
  if (s.top) {
    g.font = '700 36px "JetBrains Mono", monospace';
    const t = upper(`${s.top.name} · ${s.top.pts} pts`);
    g.textAlign = 'right'; g.fillText(t, W - 64, 165); g.textAlign = 'left';
  }
  return new Promise((ok, ko) => c.toBlob(b => (b ? ok(b) : ko(new Error('image'))), 'image/png'));
}
